import React, { useState, useEffect } from "react";
import { getPageStyles } from "../../SiteManagement/styles"; //TODO export these further up!
import {
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ssoManagementService from "../../../services/SSOManagementService";
import { SSOProviderDTO } from "@beable/roster-backend-client-v1";
import { SUBPAGES } from "../constants";
import Paging from "../Common/Paging";

interface SSOProviderListProps {
  action: "search" | "refresh" | undefined;
  selectedItem?: SSOProviderDTO;
  setSelectedItem: (item: SSOProviderDTO) => void;
  searchTerm: string;
}

export default function SSOProviderList({
  action,
  selectedItem,
  setSelectedItem,
  searchTerm,
}: SSOProviderListProps) {
  const theme = useTheme();
  const styles = getPageStyles(theme);
  const defaultState = SUBPAGES.providers.defaultState;

  const [fetchData, setFetchData] = useState(true);
  const [providersListItems, setProvidersListItems] = useState<
    SSOProviderDTO[]
  >([]);
  const [searchProvidersResult, setSearchProvidersResult] = useState<
    SSOProviderDTO[]
  >([]);
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [isLoading, setIsloading] = useState(false);

  useEffect(() => {
    const handleSearch = async () => {
      setNoSearchResults(false);
      setIsloading(true);

      if (searchTerm !== "") {
        const searchResult = await ssoManagementService.searchProviders(
          searchTerm
        );

        if (searchResult && searchResult.data.length === 0) {
          setNoSearchResults(true);
        }
        setSearchProvidersResult(searchResult?.data ?? []);
      } else {
        setSearchProvidersResult([]);
      }
      setIsloading(false);
    };

    if (action === "refresh") {
      setSearchProvidersResult([]);
      setFetchData(true);
    }

    if (action === "search") {
      handleSearch();
    }
  }, [action, searchTerm]);

  const handleTableRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    e.persist();
    e.stopPropagation();

    const selectedRow = e.currentTarget;
    const searchIsEmpty = searchTerm === "";

    const match = searchIsEmpty
      ? providersListItems.find((item) => item.id === selectedRow.id)
      : searchProvidersResult.find((item) => item.id === selectedRow.id);

    if (match && match.id !== selectedItem?.id) {
      setSelectedItem(match);
    } else {
      setSelectedItem(defaultState);
    }
  };

  useEffect(() => {
    const getAllProviders = async () => {
      setIsloading(true);
      const providersResponse = await ssoManagementService.getAllProviders();
      if (providersResponse) {
        setProvidersListItems(providersResponse.data);
      }
      setIsloading(false);
    };

    if (fetchData) {
      getAllProviders();
      setFetchData(false);
    }
  }, [fetchData]);

  const getListItems = () => {
    if (searchProvidersResult.length) {
      return searchProvidersResult;
    }

    return providersListItems;
  };

  return (
    <Container sx={styles.dfb}>
      {noSearchResults ? (
        <Typography sx={styles.mb2}>
          No search results found.
        </Typography>
      ) : undefined}
      <Paging
        filteredResultsLength={searchProvidersResult.length}
        totalPages={1}
        setPage={(_page: number) => {}}
      />
      {isLoading ? (
        <CircularProgress color="primary" />
      ) : (
        <TableContainer component={Paper} sx={styles.maxHeight}>
          <Table
            sx={styles.table}
            aria-label="providers data table"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <TableCell align="left">Name</TableCell>
                <TableCell align="left">Environment</TableCell>
                <TableCell align="left">Customer&nbsp;Identifier</TableCell>
                <TableCell align="left">User&nbsp;Identifier</TableCell>
                <TableCell align="left">
                  User&nbsp;Identifier&nbsp;Accessor
                </TableCell>
                <TableCell align="left">Redirect&nbsp;URL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getListItems()?.map((item) => (
                <TableRow
                  sx={
                    selectedItem?.id.toString() === item.id.toString()
                      ? styles.highlight
                      : undefined
                  }
                  id={item.id.toString()}
                  key={item.id}
                  onClick={handleTableRowClick}
                >
                  <TableCell align="left">{item.name}</TableCell>
                  <TableCell align="left">{item.environment}</TableCell>
                  <TableCell align="left">{item.customerIdentifier}</TableCell>
                  <TableCell align="left">{item.userIdentifier}</TableCell>
                  <TableCell align="left">
                    {item.userIdentifierAccessor}
                  </TableCell>
                  <TableCell align="left">
                    {item.redirectUrl ? item.redirectUrl : "N/A"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
